import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../AuthModule';
import AdminToolsBackToolbar from './AdminToolsBackToolbar';

const API = process.env.REACT_APP_API_URL || '';
const USER_ID_RE =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

export default function AdminCreditDonatePage() {
  const { user } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [summa, setSumma] = useState('');
  const [syy, setSyy] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const adminIdStr = user?.id != null ? String(user.id).replace(/n$/, '') : '';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!adminIdStr || !isAdmin) return;
    setUsersLoading(true);
    setUsersError(null);
    fetch(`${API}/api/admin/users/list?userId=${encodeURIComponent(adminIdStr)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Käyttäjien lataus epäonnistui.');
        return data;
      })
      .then((data) => {
        const rows = Array.isArray(data?.users) ? data.users : [];
        setUsers(rows);
      })
      .catch((err) => {
        setUsers([]);
        setUsersError(err.message || 'Käyttäjien lataus epäonnistui.');
      })
      .finally(() => setUsersLoading(false));
  }, [adminIdStr, isAdmin]);

  const filteredUsers = useMemo(() => {
    const q = String(userSearch || '').trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const dn = String(u.displayName || '').toLowerCase();
      const un = String(u.username || '').toLowerCase();
      const id = String(u.userId || '').toLowerCase();
      return dn.includes(q) || un.includes(q) || id.includes(q);
    });
  }, [users, userSearch]);

  const selectedUser = useMemo(
    () => users.find((u) => String(u.userId) === String(selectedUserId)) || null,
    [users, selectedUserId],
  );

  const onSubmit = (e) => {
    e.preventDefault();
    if (!isAdmin || !adminIdStr) return;
    setError(null);
    setStatus(null);
    const uid = String(selectedUserId).replace(/n$/, '').trim();
    if (!USER_ID_RE.test(uid)) {
      setError('Valitse kelvollinen käyttäjä listalta.');
      return;
    }
    const amount = parseInt(String(summa).trim(), 10);
    if (!Number.isFinite(amount) || amount < 1) {
      setError('Summa on positiivinen kokonaisluku.');
      return;
    }
    const reason = String(syy).trim();
    if (!reason) {
      setError('Kirjoita syy.');
      return;
    }
    setSaving(true);
    fetch(`${API}/api/admin/credits-donation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: adminIdStr,
        toUserId: uid,
        amount,
        reason,
      }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Virhe');
        return data;
      })
      .then((data) => {
        setStatus(
          `Lahjoitettu ${data.amount} PC käyttäjälle ${data.toUserId}. Uusi saldo: ${data.newBalance} PC.`,
        );
        setSelectedUserId('');
        setUserSearch('');
        setSumma('');
        setSyy('');
      })
      .catch((err) => setError(err.message || 'Virhe'))
      .finally(() => setSaving(false));
  };

  if (!user) {
    return (
      <div className="App-block Admin-tools-page">
        <AdminToolsBackToolbar />
        <p>Kirjaudu sisään.</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="App-block Admin-tools-page">
        <AdminToolsBackToolbar />
        <p>Tämä sivu on vain ylläpitäjille.</p>
      </div>
    );
  }

  return (
    <div className="App-block Admin-tools-page">
      <AdminToolsBackToolbar />

      <h1 className="Admin-tools-title">Lahjoita krediittejä</h1>

      <section className="Admin-daily-question" aria-label="Krediittilahjoitus — lomake">
        <p className="Admin-daily-question-hint">
          Hyvitys kirjataan vastaanottajan Pulina-krediitteihin. Syy näkyy heidän krediittihistoriassaan (lyhennettynä
          enintään 500 merkkiin).
        </p>

        <form className="Admin-daily-question-form" onSubmit={onSubmit}>
          <label className="Admin-daily-question-label">
            Etsi käyttäjiä nimellä
            <input
              className="Admin-daily-question-input"
              type="text"
              value={userSearch}
              onChange={(e) => {
                const v = e.target.value;
                setUserSearch(v);
                const q = String(v || '').trim().toLowerCase();
                if (!q) {
                  setSelectedUserId('');
                  return;
                }
                const exact = users.find((u) => {
                  const id = String(u.userId || '').toLowerCase();
                  const dn = String(u.displayName || '').toLowerCase();
                  const un = String(u.username || '').toLowerCase();
                  return id === q || dn === q || un === q;
                });
                if (exact) setSelectedUserId(String(exact.userId));
              }}
              autoComplete="off"
              disabled={saving}
              placeholder="Kirjoita nimi, käyttäjätunnus tai id"
            />
          </label>

          <p className="Admin-daily-question-hint">
            ID: {selectedUser ? selectedUser.userId : '-'}
          </p>

          {usersError ? (
            <p className="Admin-daily-question-error" role="alert">
              {usersError}
            </p>
          ) : null}
          {usersLoading ? <p className="Admin-daily-question-hint">Ladataan käyttäjiä…</p> : null}
          {!usersLoading && filteredUsers.length === 0 ? (
            <p className="Admin-daily-question-hint">Hakua vastaavia käyttäjiä ei löytynyt.</p>
          ) : null}
          {!usersLoading && filteredUsers.length > 0 ? (
            <label className="Admin-daily-question-label">
              Käyttäjät
              <select
                className="Admin-daily-question-input"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={saving}
                size={Math.min(10, Math.max(4, filteredUsers.length))}
              >
                <option value="">Valitse käyttäjä</option>
                {filteredUsers.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.displayName} (@{u.username}) - ID: {u.userId}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {selectedUser ? (
            <p className="Admin-daily-question-hint">
              Valittu: {selectedUser.displayName} (@{selectedUser.username}) - id {selectedUser.userId}
            </p>
          ) : null}

          <label className="Admin-daily-question-label">
            Summa (PC)
            <input
              className="Admin-daily-question-input"
              type="number"
              min={1}
              max={100000}
              step={1}
              value={summa}
              onChange={(e) => setSumma(e.target.value)}
              disabled={saving}
              placeholder="esim. 10"
            />
          </label>

          <label className="Admin-daily-question-label">
            Syy
            <input
              className="Admin-daily-question-input"
              type="text"
              value={syy}
              onChange={(e) => setSyy(e.target.value)}
              maxLength={400}
              disabled={saving}
              placeholder="Lyhyt kuvaus lahjoituksesta"
            />
          </label>

          {error ? (
            <p className="Admin-daily-question-error" role="alert">
              {error}
            </p>
          ) : null}
          {status ? (
            <p className="Admin-daily-question-status" role="status">
              {status}
            </p>
          ) : null}

          <div className="Admin-daily-question-actions">
            <button type="submit" className="Button" disabled={saving}>
              {saving ? 'Tallennetaan…' : 'Lahjoita'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
